import { useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, CircularProgress,
  FormControlLabel, Grid, Stack, TextField, Typography } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { getSpaCommunicationRules, updateSpaCommunicationRules } from '../APIs/SpaAPI';
import { EMAIL_RELEASE_EVENTS, COMMUNICATION_CHANNELS, COMMUNICATION_RULE_LIMITS, validCommunicationRules } from '../utils/spaCommunications';

export default function SpaCommunicationRules({ token, tenant, manage }) {
  const { t } = useTranslation();
  const ct = (key, options) => t(`spa.communications.${key}`, options);
  const [result, setResult] = useState(null);
  const [draft, setDraft] = useState(null);
  const [reload, setReload] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const lock = useRef(false);
  const active = useRef(false);
  useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
  useEffect(() => {
    const controller = new AbortController();
    setResult(null); setDraft(null); setError(''); setSaved(false);
    getSpaCommunicationRules(token, tenant, { signal: controller.signal }).then((data) => {
      if (!validCommunicationRules(data)) throw new Error(t('spa.communications.invalidResponse'));
      if (!controller.signal.aborted) { setResult(data); setDraft({ ...data, enabledChannels: ['EMAIL'] }); }
    }).catch((failure) => { if (!controller.signal.aborted) setError(failure.message); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenant, reload]);
  const dirty = result && draft && JSON.stringify(result) !== JSON.stringify(draft);
  const disabled = !manage || saving;
  const valid = validCommunicationRules(draft);
  useEffect(() => {
    if (!dirty && !saving) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);
  const change = (key, value) => { setSaved(false); setDraft((previous) => ({ ...previous, [key]: value })); };
  const toggle = (key, value) => change(key, draft[key].includes(value) ? draft[key].filter((item) => item !== value) : [...draft[key], value]);
  const save = async (event) => {
    event.preventDefault();
    if (!manage || !dirty || !valid || lock.current) return;
    const patch = Object.fromEntries(Object.keys(draft).filter((key) => JSON.stringify(draft[key]) !== JSON.stringify(result[key])).map((key) => [key, draft[key]]));
    lock.current = true; setSaving(true); setError(''); setSaved(false);
    try {
      const data = await updateSpaCommunicationRules({ ...patch, enabledChannels: ['EMAIL'] }, token, tenant);
      if (!active.current) return;
      if (!validCommunicationRules(data)) throw new Error(ct('invalidResponse'));
      setResult(data); setDraft({ ...data, enabledChannels: ['EMAIL'] }); setSaved(true);
    } catch (failure) { if (active.current) setError(failure.message || ct('rulesSaveError')); }
    finally { lock.current = false; if (active.current) setSaving(false); }
  };
  const numeric = (key) => <Grid item xs={12} sm={6} key={key}>
    <TextField fullWidth type="number" label={ct(`rules.${key}`)} value={draft[key]} disabled={disabled}
      error={draft[key] === '' || !Number.isFinite(draft[key]) || draft[key] < COMMUNICATION_RULE_LIMITS[key][0]
        || draft[key] > COMMUNICATION_RULE_LIMITS[key][1] || (key !== 'packageLowCreditThreshold' && !Number.isInteger(draft[key]))}
      inputProps={{ min: COMMUNICATION_RULE_LIMITS[key][0], max: COMMUNICATION_RULE_LIMITS[key][1], step: key === 'packageLowCreditThreshold' ? 'any' : 1 }}
      onChange={(event) => change(key, event.target.value === '' ? '' : Number(event.target.value))} />
  </Grid>;
  return <Box component="form" onSubmit={save}><Stack spacing={3}>
    <Typography component="h2" variant="h6">{ct('automationTitle')}</Typography>
    {error && <Alert severity="error" action={!draft && <Button color="inherit" onClick={() => setReload((value) => value + 1)}>{ct('retry')}</Button>}>{error}</Alert>}
    {!draft && !error && <CircularProgress size={24} aria-label={ct('rulesLoading')} />}
    {saved && <Alert severity="success">{ct('rulesSaved')}</Alert>}
    {draft && <>
      <Box><Typography component="h3" variant="subtitle1" fontWeight={700}>{ct('enabledEvents')}</Typography>
        <Grid container>{EMAIL_RELEASE_EVENTS.map((event) => <Grid item xs={12} sm={6} md={4} key={event}>
          <FormControlLabel label={ct(`events.${event}`)} control={<Checkbox checked={draft.enabledEvents.includes(event)}
            disabled={disabled} onChange={() => toggle('enabledEvents', event)} />} />
        </Grid>)}</Grid>
      </Box>
      <Box><Typography component="h3" variant="subtitle1" fontWeight={700}>{ct('enabledChannels')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          {COMMUNICATION_CHANNELS.map((channel) => <FormControlLabel key={channel} label={ct(`channels.${channel}`)} control={
            <Checkbox checked={channel === 'EMAIL'} disabled />
          } />)}
        </Stack>
      </Box>
      <Box><Typography component="h3" variant="subtitle1" fontWeight={700}>{ct('eligibleStatuses')}</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          {['OPTION', 'CONFIRMED', 'GUARANTEED'].map((status) => <FormControlLabel key={status} label={t(`spa.statuses.${status}`)} control={
            <Checkbox checked={draft.eligibleBookingStatuses.includes(status)} disabled={disabled} onChange={() => toggle('eligibleBookingStatuses', status)} />
          } />)}
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary">{ct('emailOnly')}</Typography>
      <Grid container spacing={2} sx={{ width: 'auto' }}>{Object.keys(COMMUNICATION_RULE_LIMITS).slice(0, 5).filter((key) => key !== 'packageExpiryNoticeDays').map(numeric)}</Grid>
      <Accordion variant="outlined" disableGutters elevation={0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{ct('recoveryTitle')}</AccordionSummary>
        <AccordionDetails><Grid container spacing={2}>{Object.keys(COMMUNICATION_RULE_LIMITS).slice(5).map(numeric)}</Grid></AccordionDetails>
      </Accordion>
      {!manage && <Typography color="text.secondary">{ct('rulesReadOnly')}</Typography>}
      {manage && <Box><Button type="submit" variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        disabled={!dirty || !valid || saving}>{ct('saveRules')}</Button>
        <Button sx={{ ml: 1 }} startIcon={<RefreshIcon />} disabled={saving || !dirty} onClick={() => { setDraft({ ...result, enabledChannels: ['EMAIL'] }); setError(''); setSaved(false); }}>{ct('discard')}</Button>
      </Box>}
    </>}
  </Stack></Box>;
}
